import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, ArrowLeft, Globe2, Monitor, Smartphone, Users, Eye, Bot,
  Download, Repeat, Clock, FileText as FileIcon, Maximize2, Copy, Check,
  ArrowRight, Share2, Layers, ChevronDown, ChevronRight,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { getPixel, getPixelStats, getPixelHits, getPixelFunnel, pixelCsvUrl, BACKEND_URL } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon, sub }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-5">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400 flex items-center gap-2">
        {Icon ? <Icon className="h-3 w-3" /> : null}{label}
      </div>
      <div className="font-display text-3xl font-semibold text-zinc-950 mt-2 tabular-nums">{value}</div>
      {sub ? <div className="text-[11px] text-zinc-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function TopList({ title, items, icon: Icon, formatKey = (k) => k, empty = "No data yet" }) {
  const max = Math.max(1, ...items.map(i => i.count));
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-5">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-4 flex items-center gap-2">
        {Icon ? <Icon className="h-3 w-3" /> : null}{title}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-zinc-400">{empty}</p>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 8).map((it) => (
            <div key={String(it.key)} className="relative">
              <div className="flex items-center justify-between text-[13px] relative z-10 px-2 py-1">
                <span className="text-zinc-900 truncate pr-3">{formatKey(it.key)}</span>
                <span className="text-zinc-500 tabular-nums font-mono">{it.count}</span>
              </div>
              <div className="absolute inset-y-0 left-0 bg-zinc-100 rounded"
                   style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function parsePageKey(raw) {
  if (raw == null) return { path: "", portal: "", screen: "", display: "" };
  let path = String(raw);
  try {
    const u = new URL(path);
    path = u.pathname + (u.search || "");
  } catch { /* already a path */ }
  if (!path) return { path: "", portal: "(direct)", screen: "", display: "(direct)" };
  if (!path.startsWith("/")) path = "/" + path;
  const [primary, ...rest] = path.split(/[?#]/, 1).length
    ? [path.split(/[?#]/)[0], path.slice(path.split(/[?#]/)[0].length)]
    : [path, ""];
  const segs = primary.split("/").filter(Boolean);
  const portal = segs[0] || "(root)";
  const screen = "/" + segs.slice(1).join("/") + (rest.join("") || "");
  const display = path;
  return { path, portal, screen: screen === "/" ? "(index)" : screen, display };
}

function PortalSparkline({ series, days = 14 }) {
  // Build a fixed-length timeline of the last `days` UTC days and look up counts.
  const today = new Date();
  const byDay = new Map((series || []).map((d) => [d.day, d.count]));
  const points = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    points.push({ day: key, count: byDay.get(key) || 0 });
  }
  const max = Math.max(1, ...points.map((p) => p.count));
  const total = points.reduce((s, p) => s + p.count, 0);
  const w = 88, h = 20, pad = 1;
  const stepX = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const pts = points.map((p, i) => [
    pad + i * stepX,
    h - pad - (p.count / max) * (h - pad * 2),
  ]);
  const linePath = pts
    .map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1))
    .join(" ");
  const areaPath = `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${h - pad} L${pts[0][0].toFixed(1)},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h}
         className="shrink-0 opacity-80"
         role="img" aria-label={`${total} hits over last ${days} days`}>
      <path d={areaPath} fill="#0055FF" opacity="0.12" />
      <path d={linePath} fill="none" stroke="#0055FF" strokeWidth="1" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function GroupedTopPages({ items, portalDaily }) {
  const [openPortals, setOpenPortals] = useState({});
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items || []) {
      const { portal, screen, display } = parsePageKey(it.key);
      if (!map.has(portal)) map.set(portal, { portal, count: 0, pages: [] });
      const g = map.get(portal);
      g.count += it.count;
      g.pages.push({ screen, display, count: it.count });
    }
    const list = Array.from(map.values())
      .map(g => ({ ...g, pages: g.pages.sort((a, b) => b.count - a.count) }))
      .sort((a, b) => b.count - a.count);
    return list;
  }, [items]);

  const grandTotal = groups.reduce((s, g) => s + g.count, 0);
  const maxGroup = Math.max(1, ...groups.map(g => g.count));

  return (
    <div className="border border-zinc-200 bg-white rounded-md p-5 md:col-span-2" data-testid="top-pages-grouped">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-4 flex items-center gap-2">
        <Layers className="h-3 w-3" /> Top pages by portal
      </div>
      {groups.length === 0 ? (
        <p className="text-xs text-zinc-400">No page data yet.</p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const open = !!openPortals[g.portal];
            const pct = grandTotal ? Math.round((g.count / grandTotal) * 100) : 0;
            return (
              <div key={g.portal} className="border border-zinc-100 rounded-md overflow-hidden"
                   data-testid={`portal-group-${g.portal}`}>
                <button
                  type="button"
                  onClick={() => setOpenPortals(p => ({ ...p, [g.portal]: !open }))}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50/80 relative text-left"
                  data-testid={`portal-group-toggle-${g.portal}`}
                >
                  <div className="absolute inset-y-0 left-0 bg-[#0055FF]/8 transition-all"
                       style={{ width: `${(g.count / maxGroup) * 100}%` }} />
                  <div className="relative z-10 flex items-center gap-2 flex-1">
                    {open ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
                          : <ChevronRight className="h-3.5 w-3.5 text-zinc-500" />}
                    <span className="text-[13px] font-semibold text-zinc-950 capitalize">{g.portal.replace(/-/g, " ")}</span>
                    <span className="text-[11px] text-zinc-500 font-mono">{g.pages.length} page{g.pages.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="relative z-10 flex items-center gap-3">
                    <PortalSparkline series={portalDaily?.[g.portal]} days={14} />
                    <span className="text-[11px] text-zinc-500 tabular-nums font-mono">{pct}%</span>
                    <span className="text-[13px] text-zinc-900 tabular-nums font-mono font-semibold min-w-[40px] text-right">{g.count}</span>
                  </div>
                </button>
                {open ? (
                  <div className="border-t border-zinc-100 bg-zinc-50/40 divide-y divide-zinc-100">
                    {g.pages.slice(0, 50).map((p, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-1.5 pl-9 text-[12px]">
                        <span className="font-mono text-zinc-700 truncate pr-3" title={p.display}>{p.screen}</span>
                        <span className="text-zinc-500 tabular-nums font-mono">{p.count}</span>
                      </div>
                    ))}
                    {g.pages.length > 50 ? (
                      <div className="px-4 py-1.5 pl-9 text-[11px] text-zinc-400">
                        + {g.pages.length - 50} more
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function DailySparkline({ daily }) {
  if (!daily?.length) return null;
  const max = Math.max(...daily.map(d => d.count), 1);
  const w = 720, h = 140, pad = 10;
  const stepX = daily.length > 1 ? (w - pad * 2) / (daily.length - 1) : 0;
  const pts = daily.map((d, i) => [pad + i * stepX, h - pad - (d.count / max) * (h - pad * 2)]);
  const areaPath = `M${pts.map(p => p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" L")} L${pts[pts.length-1][0]},${h-pad} L${pts[0][0]},${h-pad} Z`;
  const linePath = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-5">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-4">Hits per day</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-36">
        <path d={areaPath} fill="#0055FF" opacity="0.08" />
        <path d={linePath} fill="none" stroke="#09090B" strokeWidth="1.5" />
        {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.5" fill="#0055FF" />)}
      </svg>
      <div className="flex justify-between text-[10px] font-mono text-zinc-400 mt-1">
        <span>{daily[0]?.day}</span>
        <span>{daily[daily.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function HourlyHeat({ hourly }) {
  const max = Math.max(1, ...hourly.map(h => h.count));
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-5">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-4 flex items-center gap-2">
        <Clock className="h-3 w-3" /> Hour of day (UTC)
      </div>
      <div className="grid grid-cols-12 gap-1.5">
        {hourly.map((h) => {
          const pct = h.count / max;
          const bg = `rgba(0, 85, 255, ${pct ? (0.1 + pct * 0.9).toFixed(2) : 0.04})`;
          return (
            <div key={h.hour} className="aspect-square rounded relative group cursor-default"
                 style={{ background: bg }}>
              <div className="absolute inset-0 grid place-items-center text-[10px] font-mono text-zinc-600">
                {h.hour.toString().padStart(2, "0")}
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-0.5 text-[10px] font-mono text-white bg-zinc-950 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition">
                {h.hour}:00 · {h.count} hit{h.count === 1 ? "" : "s"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewVsReturning({ data }) {
  const total = (data?.new || 0) + (data?.returning || 0);
  const pctR = total ? Math.round((data.returning / total) * 100) : 0;
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-5">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-4 flex items-center gap-2">
        <Repeat className="h-3 w-3" /> New vs returning
      </div>
      {total === 0 ? (
        <p className="text-xs text-zinc-400">No data yet.</p>
      ) : (
        <>
          <div className="flex rounded-md overflow-hidden h-8">
            <div className="bg-[#0055FF] flex items-center justify-center text-white text-xs font-medium" style={{ width: `${100 - pctR}%` }}>
              {100 - pctR}%
            </div>
            <div className="bg-zinc-900 flex items-center justify-center text-white text-xs font-medium" style={{ width: `${pctR}%` }}>
              {pctR}%
            </div>
          </div>
          <div className="flex justify-between text-xs text-zinc-600 mt-3">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#0055FF]" /> New <span className="tabular-nums font-mono text-zinc-950">{data.new}</span></span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-zinc-900" /> Returning <span className="tabular-nums font-mono text-zinc-950">{data.returning}</span></span>
          </div>
        </>
      )}
    </div>
  );
}

function GAForwardingCard({ pixel }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md p-6">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Google Analytics Forwarding</div>
      <div className="flex items-center gap-3">
        <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
        <div>
          <div className="font-display text-lg font-medium text-zinc-950">{pixel?.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Forwarding to {pixel?.ga_measurement_id}
            {pixel?.has_api_secret ? " · API secret set" : " · API secret missing"}
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
        GA4 pixel hits are not stored in PortalStudio — every request is forwarded via the
        Measurement Protocol to your Google Analytics property. To see detailed charts,
        open your GA4 report at <a className="underline" href={`https://analytics.google.com/analytics/web/#/p/reports/home`} target="_blank" rel="noreferrer">analytics.google.com</a>.
      </p>
      <div className="mt-6 text-xs text-zinc-500">
        <div className="font-mono text-[11px] text-zinc-700">
          Lifetime requests forwarded: <span className="text-zinc-950 tabular-nums">{pixel?.hit_count || 0}</span>
        </div>
      </div>
    </div>
  );
}

function CopySnippet({ id }) {
  const [done, setDone] = useState(false);
  const url = `${BACKEND_URL}/api/embed/pixel/${id}.gif`;
  const snippet = `<img src="${url}" referrerpolicy="unsafe-url" width="1" height="1" alt="" style="display:none" />`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setDone(true); toast.success("Snippet copied");
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={copy} data-testid="copy-pixel-snippet"
      className="text-xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1">
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? "Copied" : "Copy embed snippet"}
    </button>
  );
}

function FunnelSection({ funnel }) {
  if (!funnel) return null;
  const sources = funnel.sources || [];
  const journeys = funnel.journeys || [];
  const maxVis = Math.max(1, ...sources.map(s => s.visitors));

  return (
    <div className="space-y-4 mb-6" data-testid="pixel-funnel-section">
      <div className="border border-zinc-200 bg-white rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 flex items-center gap-2">
            <Share2 className="h-3 w-3" /> Referrer sources
          </div>
          <div className="text-[11px] text-zinc-400">{funnel.total_visitors} visitor{funnel.total_visitors === 1 ? "" : "s"} · {funnel.direct_visitors} direct</div>
        </div>
        {sources.length === 0 ? (
          <p className="text-xs text-zinc-400">No sessions in range yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-zinc-500 text-xs">
                  <th className="pb-2 font-medium">Source</th>
                  <th className="pb-2 font-medium text-right">Visitors</th>
                  <th className="pb-2 font-medium text-right">Avg pages</th>
                  <th className="pb-2 font-medium text-right">Bounce rate</th>
                  <th className="pb-2 font-medium">Top landing pages</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.host} className="border-t border-zinc-100" data-testid={`funnel-source-${s.host}`}>
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="h-5 rounded bg-zinc-100 relative overflow-hidden" style={{ width: 80 }}>
                          <div className="absolute inset-y-0 left-0 bg-[#0055FF]/20" style={{ width: `${(s.visitors / maxVis) * 100}%` }} />
                        </div>
                        <span className="font-medium text-zinc-950">{s.host}</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-right tabular-nums font-mono">{s.visitors}</td>
                    <td className="py-2.5 text-right tabular-nums font-mono">{s.avg_depth.toFixed(1)}</td>
                    <td className="py-2.5 text-right tabular-nums font-mono">
                      <span className={s.bounce_rate >= 70 ? "text-red-600" : s.bounce_rate >= 40 ? "text-amber-600" : "text-zinc-950"}>
                        {s.bounce_rate}%
                      </span>
                    </td>
                    <td className="py-2.5 text-xs text-zinc-500 font-mono max-w-xs truncate">
                      {(s.top_landing || []).map(p => p.page).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border border-zinc-200 bg-white rounded-md p-5">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-4">
          Top visitor journeys
        </div>
        {journeys.length === 0 ? (
          <p className="text-xs text-zinc-400">No 2-hop sessions yet. Once a visitor views a second page, their journey will appear here.</p>
        ) : (
          <div className="space-y-1.5">
            {journeys.map((j, i) => (
              <div key={i} className="flex items-center gap-2 text-[13px] py-1.5 px-2 rounded hover:bg-zinc-50"
                   data-testid={`funnel-journey-${i}`}>
                <span className="text-xs text-zinc-500 font-mono w-20 truncate flex-shrink-0">{j.source}</span>
                <span className="text-zinc-400"><ArrowRight className="h-3 w-3" /></span>
                <span className="text-zinc-900 font-mono text-[12px] truncate flex-1" title={j.from_url}>{j.from}</span>
                <span className="text-zinc-400"><ArrowRight className="h-3 w-3" /></span>
                <span className="text-zinc-900 font-mono text-[12px] truncate flex-1" title={j.to_url}>{j.to}</span>
                <span className="ml-auto text-xs tabular-nums font-mono text-zinc-500 pl-2">{j.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PixelAnalytics() {
  const { id } = useParams();
  const [pixel, setPixel] = useState(null);
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [hits, setHits] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [groupByPortal, setGroupByPortal] = useState(() => {
    try { return localStorage.getItem("ps_pixel_group_by_portal") === "1"; }
    catch { return false; }
  });

  const toggleGroup = () => {
    setGroupByPortal(v => {
      const nv = !v;
      try { localStorage.setItem("ps_pixel_group_by_portal", nv ? "1" : "0"); } catch { /* noop */ }
      return nv;
    });
  };

  useEffect(() => {
    let c = false;
    setLoading(true);
    Promise.all([getPixel(id), getPixelStats(id, days), getPixelHits(id), getPixelFunnel(id, days)])
      .then(([p, s, h, f]) => {
        if (c) return;
        setPixel(p); setStats(s); setHits(h.hits || []); setFunnel(f);
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => !c && setLoading(false));
    return () => { c = true; };
  }, [id, days]);

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]"><Navbar />
        <div className="grid place-items-center h-64 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </div>
    );
  }

  const isGA = pixel?.kind === "ga";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-4" data-testid="pixel-analytics-back">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2 flex items-center gap-2">
              {isGA ? <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-900 tracking-normal text-[10px]">GA4</span>
                    : <span className="px-2 py-0.5 rounded bg-zinc-900 text-white tracking-normal text-[10px]">PORTALSTUDIO</span>}
              Analytics
            </div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
              {pixel?.name}
            </h1>
          </div>
          <div className="flex gap-2 items-center">
            {!isGA ? (
              <button
                type="button"
                onClick={toggleGroup}
                className={`h-9 px-3 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors ${
                  groupByPortal
                    ? "bg-zinc-950 text-white border-zinc-950 hover:bg-zinc-800"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
                data-testid="toggle-group-by-portal"
                title="Group top pages by the first URL segment (e.g. /parent/..., /student/...)"
              >
                <Layers className="h-3.5 w-3.5" />
                Group by portal
              </button>
            ) : null}
            {!isGA ? (
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                <SelectTrigger className="w-[160px] h-9" data-testid="pixel-range-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            ) : null}
            {!isGA ? (
              <a href={pixelCsvUrl(id)}
                 download
                 data-testid="pixel-csv-export">
                <Button variant="outline" className="h-9">
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </a>
            ) : null}
            <Link to={`/edit/${isGA ? "ga_pixel" : "ps_pixel"}/${id}`}>
              <button className="h-9 px-4 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50" data-testid="pixel-edit-btn">Edit</button>
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6 text-xs text-zinc-500">
          <code className="font-mono text-[11px] bg-zinc-100 px-2 py-1 rounded">
            {BACKEND_URL}/api/embed/pixel/{id}.gif
          </code>
          <CopySnippet id={id} />
        </div>

        {isGA ? (
          <GAForwardingCard pixel={pixel} />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Stat label="Total hits" value={stats.total_hits.toLocaleString()} icon={Eye} />
              <Stat label="Unique" value={stats.unique_visitors.toLocaleString()} icon={Users}
                    sub={stats.total_hits > 0 ? `${Math.round((stats.unique_visitors / stats.total_hits) * 100)}% of hits` : ""} />
              <Stat label="Countries" value={stats.countries.length} icon={Globe2} />
              <Stat label="Top device"
                    value={stats.devices[0]?.key || "—"}
                    icon={stats.devices[0]?.key === "Mobile" ? Smartphone : Monitor} />
              <Stat label="Bots" value={stats.bots.toLocaleString()} icon={Bot}
                    sub={stats.total_hits > 0 ? `${Math.round((stats.bots / stats.total_hits) * 100)}% of hits` : ""} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="md:col-span-2">
                <DailySparkline daily={stats.daily} />
              </div>
              <NewVsReturning data={stats.new_vs_returning} />
            </div>

            <div className="mb-6">
              <HourlyHeat hourly={stats.hourly} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <TopList title="Countries" items={stats.countries} icon={Globe2} />
              <TopList title="Cities" items={stats.cities} />
              <TopList title="Browsers" items={stats.browsers} />
              <TopList title="Operating systems" items={stats.os} />
              <TopList title="Devices" items={stats.devices} />
              <TopList title="Viewports" items={stats.viewports} icon={Maximize2}
                       empty="Set `vp` query param on pixel URL to record viewport" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {groupByPortal ? (
                <GroupedTopPages items={stats.top_pages} portalDaily={stats.portal_daily} />
              ) : (
                <TopList title="Top pages" items={stats.top_pages} icon={FileIcon}
                         formatKey={(k) => {
                           try { const u = new URL(k); return u.pathname + (u.search || ""); } catch { return k; }
                         }} />
              )}
              <TopList title="Referrers" items={stats.referrers}
                       formatKey={(k) => {
                         try { return new URL(k).hostname; } catch { return k || "direct"; }
                       }} />
            </div>

            <FunnelSection funnel={funnel} />

            <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Recent hits ({hits.length})</div>
              </div>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr className="text-left text-zinc-500 text-xs">
                      <th className="px-4 py-2 font-medium">When</th>
                      <th className="px-4 py-2 font-medium">Country</th>
                      <th className="px-4 py-2 font-medium">City</th>
                      <th className="px-4 py-2 font-medium">Browser</th>
                      <th className="px-4 py-2 font-medium">OS</th>
                      <th className="px-4 py-2 font-medium">Device</th>
                      <th className="px-4 py-2 font-medium">Page</th>
                      <th className="px-4 py-2 font-medium">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hits.slice(0, 200).map((h, i) => {
                      let refHost = "";
                      let pagePath = h.page_url;
                      try { refHost = h.referer ? new URL(h.referer).hostname : ""; } catch {}
                      try { const u = new URL(h.page_url); pagePath = u.pathname; } catch {}
                      return (
                        <tr key={i} className="border-t border-zinc-100">
                          <td className="px-4 py-2 text-zinc-500 font-mono text-[11px]">{h.ts?.replace("T", " ").slice(0, 19)}</td>
                          <td className="px-4 py-2">{h.country || "—"}</td>
                          <td className="px-4 py-2">{h.city || "—"}</td>
                          <td className="px-4 py-2">{h.browser || "—"}</td>
                          <td className="px-4 py-2">{h.os || "—"}</td>
                          <td className="px-4 py-2">
                            {h.device === "Bot" ? <span className="text-orange-700">Bot</span> : h.device || "—"}
                          </td>
                          <td className="px-4 py-2 text-zinc-500 font-mono text-[11px] max-w-[200px] truncate">{pagePath || "—"}</td>
                          <td className="px-4 py-2 text-zinc-500">{refHost || "direct"}</td>
                        </tr>
                      );
                    })}
                    {hits.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-400 text-sm">
                        No hits yet. Embed the pixel on your site to start collecting data.
                      </td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
